import type React from 'react';

import { Link, Separator, Text } from '@change/ds-core-ui';

import { Translate, TranslateHtml, useI18n } from '@change-corgi/core-react/i18n';

import { LoginEmailForm } from './components/LoginEmailForm';
import { LoginSocialButtons } from './components/LoginSocialButtons';
import { ProofStrip } from './components/ProofStrip';
import { useEmailStep } from './hooks/useEmailStep';

type InnerProps = Readonly<{
	showSocialButtons: boolean;
	socialButtonsFirst: boolean;
	sapCyclingProofTreatment: boolean;
}>;

export const EmailStep: React.FC<EmptyProps> = () => {
	const {
		data: { showSocialButtons, socialButtonsFirst, sapCyclingProofTreatment },
	} = useEmailStep();

	return (
		<div>
			<EmailStepInner
				showSocialButtons={showSocialButtons}
				socialButtonsFirst={socialButtonsFirst}
				sapCyclingProofTreatment={sapCyclingProofTreatment}
			/>
			{sapCyclingProofTreatment && <ProofStrip />}
			<Text className="mt-4" as="div" size="small" data-testid="disclaimer-text">
				<TranslateHtml
					value="corgi.login_or_signup.email.disclaimer_v3"
					replacements={{
						termsOfServiceLink: (
							<Link
								href="https://www.change.org/policies/terms-of-service"
								target="_blank"
								data-qa="terms-of-service-link"
								size="small"
							>
								<Translate value="corgi.login_or_signup.email.terms_of_service" />
							</Link>
						),
						privacyPolicyLink: (
							<Link
								href="https://www.change.org/policies/privacy"
								target="_blank"
								data-qa="privacy-policy-link"
								size="small"
							>
								<Translate value="corgi.login_or_signup.email.privacy_policy" />
							</Link>
						),
					}}
				/>
			</Text>
		</div>
	);
};

const EmailStepInner: React.FC<InnerProps> = ({ showSocialButtons, socialButtonsFirst, sapCyclingProofTreatment }) => {
	const { translate } = useI18n();

	if (!showSocialButtons) return <LoginEmailForm withSubmitButton={sapCyclingProofTreatment} />;

	if (socialButtonsFirst) {
		return (
			<>
				<LoginSocialButtons data-qa="login-social-buttons" />
				<Separator className="mt-8 mb-7" label={translate('corgi.login_or_signup.email.or')} />
				<LoginEmailForm withSubmitButton={sapCyclingProofTreatment} />
			</>
		);
	}

	return (
		<>
			<LoginEmailForm withSubmitButton={sapCyclingProofTreatment} />
			<Separator className="mt-8 mb-7" label={translate('corgi.login_or_signup.email.or')} />
			<LoginSocialButtons data-qa="login-social-buttons" />
		</>
	);
};
